import { Link } from 'react-router-dom';
import type { FailureCause, FailureInterpretation, RetryAdvice } from '../api';
import { Callout, IconButton } from './primitives';
import { targetPath, type SectionPath } from './project/operationalHealth';

const causeWord: Record<FailureCause, string> = {
  code: 'The store’s code',
  dependency: 'A dependency',
  database: 'The database',
  server_permission: 'A server permission',
  infrastructure: 'The server or its services',
  integration: 'An external service',
  commerce_ops: 'Commerce Ops itself',
  unknown: 'Not known',
};

const retryWord: Record<RetryAdvice, string> = {
  helps: 'Retrying can help',
  after_fix: 'Retry only after the fix',
  wont_help: 'Retrying the same thing will fail the same way',
  unknown: 'Whether a retry helps is not known',
  not_applicable: '',
};

/**
 * FailureInterpretationPanel answers, above the raw output, what a failed
 * run means (UX-05): what is live now, what else may have changed, the
 * likely cause — "Not known" when the server could not classify it — and
 * what to do. The raw error stays underneath for the developer. Every word
 * comes from the server (domain.InterpretDeploymentFailure /
 * InterpretRunFailure); commands are shown to copy, never run from here.
 */
export function FailureInterpretationPanel({
  interpretation,
  sectionPath,
  onShowStep,
}: {
  interpretation: FailureInterpretation;
  sectionPath?: SectionPath;
  onShowStep?: (key: string) => void;
}) {
  if (interpretation.kind === 'rollback' && interpretation.rollback) {
    return <RollbackOnRequest interpretation={interpretation} sectionPath={sectionPath} />;
  }
  const rehearsal = interpretation.kind === 'rehearsal';
  const tone = rehearsal ? 'info' : interpretation.kind === 'deployment' && interpretation.headline.startsWith('Live') ? 'warning' : 'danger';
  return (
    <Callout tone={tone} role="status" title={interpretation.headline} testId="failure-interpretation">
      <dl className="interpretation">
        <div className="interpretation__row">
          <dt>Live now</dt>
          <dd data-testid="interpretation-live">{interpretation.live_now}</dd>
        </div>
        {interpretation.also_changed ? (
          <div className="interpretation__row">
            <dt>Also changed</dt>
            <dd data-testid="interpretation-also">{interpretation.also_changed}</dd>
          </div>
        ) : null}
        <div className="interpretation__row">
          <dt>{rehearsal ? 'Why' : 'Likely cause'}</dt>
          <dd data-testid="interpretation-cause">
            {rehearsal ? null : <strong>{causeWord[interpretation.cause]}: </strong>}
            {interpretation.cause_title}
            {/[.!?]$/.test(interpretation.cause_title) ? '' : '.'}
            {interpretation.cause_detail ? <span className="interpretation__detail"> {interpretation.cause_detail}</span> : null}
          </dd>
        </div>
        {interpretation.failed_step ? (
          <div className="interpretation__row">
            <dt>Failed at</dt>
            <dd>
              {onShowStep ? (
                <button type="button" className="link-button" onClick={() => onShowStep(interpretation.failed_step!.key)}>
                  Step {interpretation.failed_step.sequence} · {interpretation.failed_step.title}
                </button>
              ) : (
                <>Step {interpretation.failed_step.sequence} · {interpretation.failed_step.title}</>
              )}
            </dd>
          </div>
        ) : null}
        {retryWord[interpretation.retry] ? (
          <div className="interpretation__row">
            <dt>Retry</dt>
            <dd data-testid="interpretation-retry">
              {retryWord[interpretation.retry]}
              {interpretation.retry_why ? `: ${interpretation.retry_why.charAt(0).toLowerCase()}${interpretation.retry_why.slice(1)}` : '.'}
            </dd>
          </div>
        ) : null}
      </dl>

      {interpretation.actions.length > 0 ? (
        <div className="interpretation__actions">
          <strong className="interpretation__label">What to do</strong>
          <ol className="interpretation__steps" data-testid="interpretation-actions">
            {interpretation.actions.map((action, index) => (
              <li key={`${action.label}-${index}`}>
                {action.target && sectionPath ? (
                  <Link to={targetPath(action.target, sectionPath)}>{action.label}</Link>
                ) : (
                  <span>{action.label}</span>
                )}
                {action.detail && action.detail !== action.label ? <span className="muted"> — {action.detail}</span> : null}
                {action.command ? (
                  <div className="interpretation__command">
                    <code>{action.command}</code>
                    <IconButton
                      icon="copy"
                      label="Copy the command"
                      onClick={() => {
                        void navigator.clipboard?.writeText(action.command!);
                      }}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {interpretation.raw_error ? (
        <details className="disclosure interpretation__raw">
          <summary>The raw error</summary>
          <pre className="command-block">
            <code>{interpretation.raw_error}</code>
          </pre>
        </details>
      ) : null}
    </Callout>
  );
}

/**
 * A completed deployment that a person rolled back later is not a failure
 * (CO-140): it says what the deployment did, who switched back, when, why
 * and to what. "Rolled back" keeps its warning tone (UX_TERMINOLOGY).
 */
function RollbackOnRequest({ interpretation, sectionPath }: { interpretation: FailureInterpretation; sectionPath?: SectionPath }) {
  const rollback = interpretation.rollback!;
  return (
    <Callout tone="warning" role="status" title={interpretation.headline} testId="failure-interpretation">
      <dl className="interpretation">
        {interpretation.outcome ? (
          <div className="interpretation__row">
            <dt>Deployment</dt>
            <dd data-testid="interpretation-outcome">{interpretation.outcome}</dd>
          </div>
        ) : null}
        <div className="interpretation__row">
          <dt>Rolled back</dt>
          <dd data-testid="interpretation-rollback">
            By <strong>{rollback.initiated_by}</strong> on{' '}
            <time dateTime={rollback.at}>{new Date(rollback.at).toLocaleString()}</time>, to {rollback.to}.
          </dd>
        </div>
        {rollback.reason ? (
          <div className="interpretation__row">
            <dt>Reason</dt>
            <dd data-testid="interpretation-reason">{rollback.reason}</dd>
          </div>
        ) : null}
        <div className="interpretation__row">
          <dt>Live now</dt>
          <dd data-testid="interpretation-live">{interpretation.live_now}</dd>
        </div>
        {interpretation.also_changed ? (
          <div className="interpretation__row">
            <dt>Database</dt>
            <dd data-testid="interpretation-also">{interpretation.also_changed}</dd>
          </div>
        ) : null}
      </dl>
      {interpretation.actions.length > 0 && sectionPath ? (
        <div className="interpretation__links" data-testid="interpretation-actions">
          {interpretation.actions.map((action, index) =>
            action.target ? (
              <Link key={`${action.label}-${index}`} to={targetPath(action.target, sectionPath)}>
                {action.label}
              </Link>
            ) : null,
          )}
        </div>
      ) : null}
    </Callout>
  );
}
